import api from "@/apiConfig/api";
import apiV2 from "@/apiConfig/api-v2";
import { ElMessage } from "element-plus";
import { storageGetJSON } from "@/services/storage.service";

// Deduplication: in-flight promise cache keyed by params so identical simultaneous calls share one request
let statisticsPromise = null;
let detailedStatisticsPromise = null;
let getUsersPromise = null;
let getStudentsPromise = null;

export default {
  namespaced: true,

  state() {
    return {
      dailyTasksFilterStatus: storageGetJSON("dailyTasksFilterStatus", null),
      students: [],
      pages: {
        students: 1,
      },
      users: [],
      user: null,
      avgScore: null,
      currentDate: null,
      history: [],
      dailyTransactions: [],
      createGroupDialog: false,
      courses: [],
      rooms: [],
      statistics: [],
      statisitcsLoading: true,
      studentLoading: true,
    };
  },

  mutations: {
    SET_FILTER_STATUS(state, status) {
      state.dailyTasksFilterStatus = status;
    },
    CLEAR_FILTER_STATUS(state) {
      state.dailyTasksFilterStatus = null;
    },
    SET_STUDENTS(state, students) {
      state.students = students;
    },
    SET_STUDENT_PAGES(state, pageCount) {
      state.pages.students = pageCount;
    },
    SET_CREATE_GROUP_DIALOG(state, value) {
      state.createGroupDialog = value;
    },
    SET_STATISTICS_LOADING(state, loading) {
      state.statisitcsLoading = loading;
    },
  },

  actions: {
    setFilterStatus({ commit }, status) {
      localStorage.setItem("dailyTasksFilterStatus", JSON.stringify(status));
      commit("SET_FILTER_STATUS", status);
    },

    deleteFilterStatus({ commit }) {
      localStorage.removeItem("dailyTasksFilterStatus");
      commit("CLEAR_FILTER_STATUS");
    },

    async getStudents(
      { commit },
      {
        branch_id,
        page,
        name,
        last_status,
        last_status_start_date,
        last_status_end_date,
      }
    ) {
      const params = { branch_id, page, per_page: 50 };
      if (name !== undefined && name !== null && name !== "") {
        params.name = name;
      }
      if (last_status !== undefined && last_status !== null) {
        params.last_status = last_status;
      }
      if (last_status_start_date) {
        params.last_status_start_date = last_status_start_date;
      }
      if (last_status_end_date) {
        params.last_status_end_date = last_status_end_date;
      }
      const cacheKey = JSON.stringify(params);
      if (getStudentsPromise?.key === cacheKey) {
        const data = await getStudentsPromise.promise;
        commit("SET_STUDENTS", data?.students || []);
        commit("SET_STUDENT_PAGES", data?.page_count || 1);
        return;
      }
      const promise = api
        .get("/student/search", { params })
        .then((res) => res.data)
        .finally(() => {
          getStudentsPromise = null;
        });
      getStudentsPromise = { key: cacheKey, promise };
      const data = await promise;
      commit("SET_STUDENTS", data?.students || []);
      commit("SET_STUDENT_PAGES", data?.page_count || 1);
    },

    async getUsers(ctx, { branch_id, role, name }) {
      const payload = { branch_id, role, name };
      const cacheKey = JSON.stringify(payload);
      if (getUsersPromise?.key === cacheKey) {
        ctx.state.users = await getUsersPromise.promise;
        return;
      }
      const promise = api
        .get("/users", {
          params: { role, branch_id, name },
        })
        .then((res) => res.data)
        .catch(() => [])
        .finally(() => {
          getUsersPromise = null;
        });
      getUsersPromise = { key: cacheKey, promise };
      ctx.state.users = await promise;
    },
    setUser(ctx, user) {
      ctx.state.user = user;
    },
    setAvgScore(ctx, score) {
      ctx.state.avgScore = score;
    },
    setCurrentDate(ctx, date) {
      ctx.state.currentDate = date;
    },
    async getStudent(ctx, student_id) {
      ctx.state.studentLoading = true;
      try {
        const res = await api.get(`/student/${student_id}`);
        ctx.state.user = res.data;
      } catch (error) {
        console.error("Error fetching student data:", error);
      }
      ctx.state.studentLoading = false;
    },
    async getHistory(ctx, student_id) {
      try {
        const res = await apiV2.get(`/student/status/history/${student_id}`);
        ctx.state.history = res.data;
      } catch (error) {
        console.error("Error fetching student history:", error);
        ctx.state.history = [];
      }
    },
    async getDailyTransactions(ctx, { student_id, year, month }) {
      try {
        const res = await apiV2.get(`/payments/monthly-history/${student_id}`, {
          params: { student_id, year, month },
        });
        ctx.state.dailyTransactions = res.data || [];
      } catch (error) {
        console.error("Error fetching daily transactions:", error);
        ctx.state.dailyTransactions = [];
      }
    },
    async getCourses(ctx) {
      await api
        .get("/courses/all")
        .then((res) => {
          ctx.state.courses = res.data || [];
        })
        .catch(() => {
          ctx.state.courses = [];
        });
    },
    async getRooms(ctx, { branch_id }) {
      await api
        .get("/company/room", {
          params: { branch_id },
        })
        .then((res) => {
          ctx.state.rooms = res.data || [];
        })
        .catch(() => {
          ctx.state.rooms = [];
        });
    },
    async createGroup(ctx, data) {
      try {
        const res = await apiV2.post("/groups", data);
        ctx.commit("SET_CREATE_GROUP_DIALOG", false);
        ElMessage({
          type: "success",
          message: "Guruh muvaffaqiyatli yaratildi",
        });
      } catch (error) {
        console.error("Error creating group:", error);
        ElMessage({
          type: "error",
          message: "Guruh yaratishda xatolik yuz berdi",
        });
        throw error;
      }
    },
    async editGroup(ctx, { groupId, data }) {
      try {
        const res = await api.put(`/groups/${groupId}`, data);
        ctx.commit("SET_CREATE_GROUP_DIALOG", false);
        ElMessage({
          type: "success",
          message: "Guruh muvaffaqiyatli tahrirlandi",
        });
      } catch (error) {
        console.error("Error editing group:", error);
        ElMessage({
          type: "error",
          message: "Guruhni tahrirlashda xatolik yuz berdi",
        });

        throw error;
      }
    },
    async getStatistics(ctx, { branch_id, filter_date }) {
      const payload = { branch_id };
      if (filter_date) payload.filter_date = filter_date;
      const cacheKey = JSON.stringify(payload);
      if (statisticsPromise?.key === cacheKey) {
        return statisticsPromise.promise;
      }
      ctx.commit("SET_STATISTICS_LOADING", true);
      const promise = apiV2
        .get("/reports/admin-dashboard-statistics", {
          params: payload,
        })
        .then((res) => {
          ctx.state.statistics = res.data || [];
          return ctx.state.statistics;
        })
        .catch((error) => {
          console.error("Error fetching statistics:", error);
          ctx.state.statistics = [];
          return [];
        })
        .finally(() => {
          ctx.commit("SET_STATISTICS_LOADING", false);
          statisticsPromise = null;
        });
      statisticsPromise = { key: cacheKey, promise };
      return promise;
    },
    async getDetailedStatistics(ctx, { branch_id, search, signal, status, filter_date }) {
      const params = { branch_id };
      if (search !== undefined && search !== null && search !== "") {
        params.search = search;
      }
      if (status !== undefined && status !== null) {
        params.status = status;
      }
      if (filter_date) {
        params.filter_date = filter_date;
      }
      const cacheKey = JSON.stringify(params);
      if (detailedStatisticsPromise?.key === cacheKey) {
        return detailedStatisticsPromise.promise;
      }
      const promise = apiV2
        .get("/reports/admin-dashboard-detailed-statistics", { params, signal })
        .then((res) => res.data)
        .catch((error) => {
          if (error?.name === "AbortError") throw error;
          console.error("Error fetching detailed statistics:", error);
          return null;
        })
        .finally(() => {
          detailedStatisticsPromise = null;
        });
      detailedStatisticsPromise = { key: cacheKey, promise };
      return promise;
    },
  },
  getters: {
    getHistory:
      (state) =>
      (filters = []) => {
        const history = Array.isArray(state.history) ? state.history : [];

        if (!Array.isArray(filters) || filters.length === 0) {
          return history;
        }

        return history.filter((item) => item && filters.includes(item.action));
      },
    getDailyTransactions: (state) => {
      return state.dailyTransactions || [];
    },
  },
};
